import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ExportFileType, StaleTime, QueryTag } from '@common/ts/enums';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import axiosInstance from '@/axios/axiosInstance';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import { API_ENDPOINTS } from '@/api-endpoints';
import {
  AllTranscriptsProps,
  CategoryApiResponse,
  FiltersDataToSendProps,
  Insights,
  Organization,
  OverviewData,
  Project,
  ProjectProps,
  Transcripts,
  User,
  WorkspaceMemberSubset,
  ThemeSubset,
  Theme,
  OrganizationMemberSubset,
  PaginationConfig,
  PaginatedResult,
  PaginatedApiResponse,
  WorkspaceGroupsResponse,
  WorkspaceWithProjectCount,
  FiltersConfig,
  SortConfig,
  InterviewQuestion,
  InsightsSubset,
  AiSearchResults,
  AiSearchResultsSubset,
  ProjectMetadata,
  GetConversationsPageParams,
  ExportParams,
  QuotaStatusResponse,
  ScreeningStatsResponse,
} from '@/types';
import { ECategoryMode, TranscriptsView } from '@/enums';
import { useMutateApi, useMutateApiWithStore } from './post';
import useCreateProjectStore from '@/store/CreateProjectStore';
import useUserStore from '@/store/UserStore';
import { useCurrentConversations } from '@/hooks/useCurrentConversations';

const fetchData = async <T>(
  endpoint: string,
  instance = axiosInstance,
  token = null as string | null,
): Promise<T> => {
  const { data } = await instance.get(
    endpoint,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
  );
  return data;
};

export const useGetUser = (enabled = false) => {
  const { setUser, setIsLoading } = useUserStore();

  const {
    data: user,
    refetch,
    isPending,
  } = useQuery<User>({
    queryKey: QUERY_KEYS.USER,
    queryFn: () => fetchData<User>(API_ENDPOINTS.USERS.GET_USER_DATA),
    enabled,
  });

  useEffect(() => {
    if (user) setUser(user);
    if (enabled) setIsLoading(isPending);
  }, [user, setUser, isPending, setIsLoading, enabled]);

  return { data: user, refetch };
};

export const useGetUserWithToken = () =>
  useMutation({
    mutationFn: (token: string) =>
      fetchData<User>(API_ENDPOINTS.USERS.GET_USER_DATA, axiosInstanceNoAuth, token),
  });

export const useValidateInvitation = () =>
  useMutation({
    mutationFn: ({ organizationId, token }: { organizationId: string; token: string }) =>
      fetchData<{ valid: boolean }>(
        API_ENDPOINTS.ORGANIZATIONS.VALIDATE_INVITE(organizationId),
        axiosInstanceNoAuth,
        token,
      ),
  });

export const useGetTranslationLanguages = (projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_LANGUAGES(projectId),
    queryFn: () =>
      fetchData<string[]>(API_ENDPOINTS.RESULTS.GET_LANGUAGES(projectId!), axiosInstanceAnalyze),
    enabled: !!projectId,
    staleTime: StaleTime.VERY_LONG,
  });

/**
 * TELV1-501 — per-language translation coverage. Powers the transcripts
 * "translated X of Y" banner (untranslated conversations silently drop out
 * of the translated list view, so without this the gap is invisible) and
 * the once-per-session self-heal re-trigger.
 */
export type TranslationSummaryEntry = {
  language: string;
  translated: number;
  total: number;
  failed: number;
};

export const useGetTranslationSummary = (projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.TRANSLATION_SUMMARY(projectId),
    queryFn: () =>
      fetchData<TranslationSummaryEntry[]>(
        API_ENDPOINTS.RESULTS.GET_TRANSLATION_SUMMARY(projectId!),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.SHORT,
  });

export const useGetTranslationProgress = (projectId?: string, language?: string | null) =>
  useQuery({
    queryKey: QUERY_KEYS.TRANSLATION_PROGRESS(projectId, language ?? undefined),
    queryFn: () =>
      fetchData<{ count: number }>(
        API_ENDPOINTS.RESULTS.GET_TRANSLATION_PROGRESS(projectId!, language!),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId && !!language,
    refetchInterval: 5000,
    // TELV1-469 — poll even when the window is unfocused. Without this, TanStack
    // gates the interval's fetch behind focusManager.isFocused(), so the moment
    // the researcher tabs away while a translation runs the progress poll pauses.
    // The completion check needs 3 consecutive polls to detect a stable count, so
    // a paused poll means the loader never resolves and the transcripts view hangs
    // on a spinner until a manual refresh. Self-limiting: the query is only enabled
    // while a translation is pending (language set), so this is a short, scoped poll.
    refetchIntervalInBackground: true,
    retry: 3,
  });

export const useGetOrganizationsOnLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      fetchData<PaginatedApiResponse<Organization>>(
        API_ENDPOINTS.ORGANIZATIONS.GET_ALL,
        axiosInstanceNoAuth,
        token,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.ORGANIZATIONS, data);
    },
  });
};

export const useGetOrganizations = () =>
  useQuery<PaginatedApiResponse<Organization>, unknown, PaginatedResult<Organization>>({
    queryKey: QUERY_KEYS.ORGANIZATIONS,
    queryFn: () =>
      fetchData<PaginatedApiResponse<Organization>>(API_ENDPOINTS.ORGANIZATIONS.GET_ALL),
    staleTime: StaleTime.VERY_LONG,
    select: ({ data, pagination }) => ({ items: data, pagination }),
  });

export const useGetAllProjects = (
  organizationId?: string,
  workspaceId?: string,
  paginationConfig?: PaginationConfig,
  filtersConfig?: FiltersConfig,
  sortConfig?: SortConfig,
) =>
  useQuery<PaginatedApiResponse<Project>, unknown, PaginatedResult<Project>>({
    queryKey: QUERY_KEYS.PROJECTS(workspaceId!, paginationConfig?.page, filtersConfig, sortConfig),
    enabled: !!organizationId && !!workspaceId,
    queryFn: () =>
      fetchData<PaginatedApiResponse<Project>>(
        API_ENDPOINTS.PROJECTS.GET_ALL(
          organizationId!,
          workspaceId!,
          paginationConfig,
          filtersConfig,
          sortConfig,
        ),
      ),
    staleTime: StaleTime.MEDIUM,
    select: ({ data, pagination }) => ({ items: data, pagination }),
  });

export const useGetWorkspaceMembers = (
  organizationId?: string,
  workspaceId?: string,
  enabled = true,
  paginationConfig?: PaginationConfig,
) =>
  useQuery<
    PaginatedApiResponse<WorkspaceMemberSubset>,
    unknown,
    PaginatedResult<WorkspaceMemberSubset>
  >({
    queryKey: QUERY_KEYS.WORKSPACE_MEMBERS(workspaceId),
    enabled: !!workspaceId && !!organizationId && enabled,
    queryFn: () =>
      fetchData<PaginatedApiResponse<WorkspaceMemberSubset>>(
        API_ENDPOINTS.WORKSPACES.GET_MEMBERS(organizationId!, workspaceId!, paginationConfig),
      ),
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({
      items: data.map(({ _id, email, name, role, email_verified }) => ({
        _id,
        email,
        name,
        role,
        email_verified,
      })),
      pagination,
    }),
  });

export const useGetWorkspaceGroups = (organizationId?: string) =>
  useQuery<PaginatedApiResponse<WorkspaceWithProjectCount>, unknown, WorkspaceGroupsResponse>({
    queryKey: QUERY_KEYS.WORKSPACE_GROUPS(organizationId),
    queryFn: () =>
      fetchData<PaginatedApiResponse<WorkspaceWithProjectCount>>(
        API_ENDPOINTS.WORKSPACES.GET_ALL(organizationId!, { page: 1, limit: 250 }),
      ),
    enabled: !!organizationId,
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({ items: data, pagination }),
  });

export const useGetOrganizationMembers = (
  organizationId?: string,
  paginationConfig?: PaginationConfig,
  filtersConfig?: FiltersConfig,
  sortConfig?: SortConfig,
) =>
  useQuery<
    PaginatedApiResponse<OrganizationMemberSubset>,
    unknown,
    PaginatedResult<OrganizationMemberSubset>
  >({
    queryKey: QUERY_KEYS.ORGANIZATION_MEMBERS(
      organizationId!,
      paginationConfig?.page,
      filtersConfig,
      sortConfig,
    ),
    enabled: !!organizationId,
    queryFn: () =>
      fetchData<PaginatedApiResponse<OrganizationMemberSubset>>(
        API_ENDPOINTS.ORGANIZATIONS.GET_MEMBERS(
          organizationId!,
          paginationConfig,
          filtersConfig,
          sortConfig,
        ),
      ),
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({
      items: data.map(
        ({ _id, email, name, invite_accepted, email_verified, updatedAt, invitations, role }) => ({
          _id,
          email,
          name,
          invite_accepted,
          email_verified,
          updatedAt,
          invitations,
          role,
        }),
      ),
      pagination,
    }),
    placeholderData: (previousData) => previousData,
  });

export const useGetThemes = (
  organizationId?: string,
  paginationConfig?: PaginationConfig,
  filtersConfig?: FiltersConfig,
  sortConfig?: SortConfig,
) =>
  useQuery<PaginatedApiResponse<ThemeSubset>, unknown, PaginatedResult<ThemeSubset>>({
    queryKey: QUERY_KEYS.THEMES(organizationId!, paginationConfig?.page, filtersConfig, sortConfig),
    enabled: !!organizationId,
    queryFn: () =>
      fetchData<PaginatedApiResponse<ThemeSubset>>(
        API_ENDPOINTS.THEMES.GET_ALL(organizationId!, paginationConfig, filtersConfig, sortConfig),
      ),
    staleTime: StaleTime.LONG,
    select: ({ data, pagination }) => ({
      items: data.map(({ _id, name, createdAt }) => ({
        _id,
        name,
        createdAt,
      })),
      pagination,
    }),
    placeholderData: (previousData) => previousData,
  });

export const useGetTheme = (organizationId?: string, themeId?: string | null) =>
  useQuery({
    queryKey: QUERY_KEYS.THEME(organizationId, themeId),
    enabled: !!organizationId && !!themeId,
    queryFn: () => fetchData<Theme>(API_ENDPOINTS.THEMES.GET_BY_ID(organizationId!, themeId!)),
    staleTime: StaleTime.LONG,
  });

export const useGetSingleProjectData = (
  organizationId: string,
  workspaceId: string,
  projectId: string,
  options?: { enabled?: boolean },
) => {
  const { isMediaReadyForUpload } = useCreateProjectStore();
  const defaultEnabled = !!projectId && !!workspaceId && !!organizationId && !isMediaReadyForUpload;
  const isQueryEnabled =
    options?.enabled !== undefined ? options.enabled && defaultEnabled : defaultEnabled;

  return useQuery<ProjectProps, unknown>({
    queryKey: [QueryTag.SINGLE_PROJECT_DATA, projectId],
    enabled: isQueryEnabled,
    queryFn: () =>
      fetchData<ProjectProps>(
        API_ENDPOINTS.PROJECTS.GET_BY_ID(organizationId, workspaceId, projectId),
      ),
    select: (data) => data,
    retry: 0,
  });
};

export const useGetOverviewData = (filterId: string | null, projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.INTERVIEW_OVERVIEW(projectId, filterId),
    queryFn: () =>
      fetchData<OverviewData>(
        API_ENDPOINTS.RESULTS.GET_OVERALL(projectId!, filterId),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    refetchOnWindowFocus: true,
    staleTime: StaleTime.MEDIUM,
  });

export const useGetInterviewQuestions = (filterId: string | null, projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.INTERVIEW_QUESTIONS(projectId, filterId),
    queryFn: () =>
      fetchData<InterviewQuestion[]>(
        API_ENDPOINTS.RESULTS.GET_INTERVIEW_QUESTIONS(projectId!, filterId),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useGetProjectQuestionInsights = (
  filterId: string | null,
  projectId?: string,
  questionId?: string,
) =>
  useQuery<Insights, unknown, InsightsSubset>({
    queryKey: QUERY_KEYS.INTERVIEW_QUESTION_INSIGHTS(projectId, questionId, filterId),
    enabled: !!questionId && !!projectId,
    queryFn: () =>
      fetchData<Insights>(
        API_ENDPOINTS.RESULTS.GET_PER_QUESTION_INSIGHTS(projectId!, questionId!, filterId),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.LONG,
    select: ({
      summary,
      patterns_and_trends,
      themes,
      divergent_views_and_outliers,
      opportunities,
      quotes,
      visual_observations,
      question_visuals,
      voice_quotes,
    }) => {
      return {
        summary,
        patterns_and_trends,
        themes,
        divergent_views_and_outliers,
        opportunities,
        quotes,
        visual_observations,
        question_visuals,
        voice_quotes,
      };
    },
  });

export const useGetInterviewQuestionTransripts = (
  filterId: string | null,
  categoryType: ECategoryMode | null,
  projectId?: string,
  questionId?: string,
  language?: string | null,
) =>
  useQuery({
    queryKey: QUERY_KEYS.QUESTION_TRANSCRIPTS(
      filterId,
      categoryType,
      projectId,
      questionId,
      language,
    ),
    enabled: !!questionId && categoryType !== undefined,
    queryFn: () =>
      fetchData<Transcripts[]>(
        API_ENDPOINTS.RESULTS.GET_PER_QUESTION_TRANSCRIPTS(
          projectId!,
          questionId!,
          categoryType,
          filterId,
          language,
        ),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.MEDIUM,
  });

/**
 * Shape returned by the new `/transcripts/page` backend endpoint.
 * Mirrors `PaginatedResponse<Transcripts>` from @app/common/utils.
 */
export interface PaginatedTranscriptsResponse {
  data: Transcripts[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * Paginated + filterable + sortable + searchable per-question
 * transcripts. Hits the `/transcripts/page` backend route so the
 * legacy array-returning `/transcripts` route stays intact for
 * external API consumers.
 *
 * Passing filters/search/sort to the server means large projects
 * (30k+ respondents) no longer pay the cost of fetching every
 * conversation and transforming them client-side.
 */
export const useGetInterviewQuestionTranscriptsPaginated = (params: {
  filterId: string | null;
  categoryType: ECategoryMode | null;
  projectId?: string;
  questionId?: string;
  language?: string | null;
  page?: number;
  limit?: number;
  search?: string;
  categoryIds?: string[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** Allow the caller to disable the query (e.g. before questionId resolves). */
  enabled?: boolean;
}) =>
  useQuery<PaginatedTranscriptsResponse>({
    queryKey: QUERY_KEYS.QUESTION_TRANSCRIPTS_PAGE({
      filterId: params.filterId,
      categoryType: params.categoryType,
      projectId: params.projectId,
      questionId: params.questionId,
      language: params.language,
      page: params.page,
      limit: params.limit,
      search: params.search,
      categoryIds: params.categoryIds,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    }),
    enabled: (params.enabled ?? true) && !!params.questionId && params.categoryType !== undefined,
    queryFn: () =>
      fetchData<PaginatedTranscriptsResponse>(
        API_ENDPOINTS.RESULTS.GET_PER_QUESTION_TRANSCRIPTS_PAGE({
          projectId: params.projectId,
          questionId: params.questionId,
          categoryType: params.categoryType,
          filterId: params.filterId,
          language: params.language,
          page: params.page,
          limit: params.limit,
          search: params.search,
          categoryIds: params.categoryIds,
          sortBy: params.sortBy,
          sortDirection: params.sortDirection,
        }),
        axiosInstanceAnalyze,
      ),
    // `placeholderData: keepPreviousData` pattern: keep the previous
    // page's data while the next page loads so the panel doesn't flash
    // to empty during pagination. Tanstack Query v4 used
    // `keepPreviousData: true`; v5 renamed to placeholderData.
    placeholderData: (prev) => prev,
    staleTime: StaleTime.MEDIUM,
  });

export const useGetAISearchResults = (projectId: string | undefined, filterId: string | null) =>
  useQuery<AiSearchResults, unknown, AiSearchResultsSubset>({
    queryKey: QUERY_KEYS.AI_SEARCH_RESULTS(projectId, filterId),
    queryFn: () =>
      fetchData<AiSearchResults>(
        API_ENDPOINTS.AI_SEARCH.GET(projectId!, filterId),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.MEDIUM,
    enabled: !!projectId,
    select: ({ searches, project_id, _id }) => ({
      searches,
      project_id,
      _id,
    }),
  });

export const useGetFilters = (projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_FILTERS(projectId),
    queryFn: () =>
      fetchData<FiltersDataToSendProps[]>(
        API_ENDPOINTS.FILTERS.GET_BY_ID(projectId!),
        axiosInstanceAnalyze,
      ),
    staleTime: StaleTime.LONG,
  });

export const useGetProjectMetadata = (projectId: string | undefined) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_METADATA(projectId),
    queryFn: () =>
      fetchData<ProjectMetadata[]>(
        API_ENDPOINTS.FILTERS.GET_METADATA(projectId!),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

/**
 * Project-level respondent_index → conversation_id lookup. Fetched
 * once per project and cached. The Research Assistant citation pills
 * use this so `[R33]` opens the matching transcript without a
 * per-click round-trip.
 *
 * Payload is small (~40 bytes per respondent). LONG stale-time is
 * safe because the mapping is stable once interviews complete —
 * respondent_index doesn't change after analysis.
 */
export const useGetProjectRespondentLookup = (projectId: string | undefined) =>
  useQuery({
    queryKey: QUERY_KEYS.PROJECT_RESPONDENT_LOOKUP(projectId),
    queryFn: () =>
      fetchData<Array<{ respondent_index: number; conversation_id: string }>>(
        API_ENDPOINTS.FILTERS.GET_RESPONDENT_LOOKUP(projectId!),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useGetQuestionCategories = (
  filterId: string | null,
  projectId?: string,
  questionId?: string,
  enablePolling?: boolean,
) =>
  useQuery<CategoryApiResponse>({
    queryKey: QUERY_KEYS.INTERVIEW_QUESTION_CATEGORIES(projectId, questionId, filterId),
    queryFn: () =>
      fetchData<CategoryApiResponse>(
        API_ENDPOINTS.CATEGORIES.GET_BY_QUESTION(projectId!, questionId!, filterId),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId && !!questionId,
    staleTime: enablePolling ? 0 : StaleTime.LONG,
    refetchInterval: enablePolling ? 10000 : false, // Poll every 10 seconds if enabled
    refetchOnWindowFocus: !!enablePolling,
  });

export const useGetConversation = (projectId?: string, conversationId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.CONVERSATION(projectId, conversationId),
    queryFn: () =>
      fetchData<AllTranscriptsProps>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATION_BY_ID(projectId, conversationId),
        axiosInstanceAnalyze,
      ),
    enabled: !!conversationId && !!projectId,
    staleTime: StaleTime.SHORT,
  });

export const useGetConversationPanel = (projectId?: string, conversationId?: string | null) =>
  useQuery({
    queryKey: ['conversation-panel', { projectId, conversationId }],
    queryFn: () =>
      fetchData<AllTranscriptsProps>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATION_PANEL(projectId, conversationId!),
        axiosInstanceAnalyze,
      ),
    enabled: !!conversationId && !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useMutateDownloadConversation = (projectId?: string) => {
  return useMutateApi(
    ({
      conversationId,
      fileType,
      language,
    }: {
      conversationId: string;
      fileType?: ExportFileType;
      language?: string;
    }) =>
      axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION(projectId, conversationId, fileType, language),
        { responseType: 'blob' },
      ),
  );
};

export const useMutateDownloadConversationDetails = () => {
  return useMutateApi(({ projectId, filterId, fileType, transcriptsView }: ExportParams) =>
    axiosInstanceAnalyze.get(
      API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION_DETAILS(
        projectId,
        filterId,
        fileType,
        transcriptsView,
      ),
      { responseType: 'blob' },
    ),
  );
};

export const useMutateDownloadConversationOverview = () => {
  return useMutateApi(({ projectId, filterId, fileType, transcriptsView }: ExportParams) =>
    axiosInstanceAnalyze.get(
      API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION_OVERVIEW(
        projectId,
        filterId,
        fileType,
        transcriptsView,
      ),
      { responseType: 'blob' },
    ),
  );
};

export const useMutateDownloadFullAnswersTranscript = () => {
  return useMutateApi(({ projectId, filterId, fileType, transcriptsView }: ExportParams) =>
    axiosInstanceAnalyze.get(
      API_ENDPOINTS.RESULTS.EXPORT_FULL_ANSWERS_TRANSCRIPT(
        projectId,
        filterId,
        fileType,
        transcriptsView,
      ),
      { responseType: 'blob' },
    ),
  );
};

export const useMutateDownloadAnalysisReport = () => {
  return useMutateApi(({ projectId, filterId }: { projectId: string; filterId?: string | null }) =>
    axiosInstanceAnalyze.get(API_ENDPOINTS.RESULTS.EXPORT_ANALYSIS(projectId, filterId), {
      responseType: 'blob',
    }),
  );
};

export const useMutateDownloadConversationMedia = () => {
  return useMutation({
    mutationFn: async ({
      projectId,
      conversationId,
      filename,
    }: {
      projectId: string;
      conversationId: string;
      filename: string;
    }) => {
      const response = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESULTS.EXPORT_CONVERSATION_MEDIA(projectId, conversationId),
        { responseType: 'blob' },
      );
      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useGetAllConversations = (
  projectId: string | undefined,
  filterId: string | null,
  shouldFetch: boolean,
  language: string | null,
  refetchInterval: number,
  conversationFilter?: TranscriptsView,
  paginationConfig?: PaginationConfig,
  sortConfig?: SortConfig,
  searchFilters?: Record<string, string>,
) =>
  useQuery<
    PaginatedApiResponse<AllTranscriptsProps>,
    unknown,
    PaginatedResult<AllTranscriptsProps>
  >({
    queryKey: [
      QueryTag.CONVERSATIONS,
      {
        projectId,
        filterId,
        language,
        conversationFilter,
        page: paginationConfig?.page,
        sortConfig,
        searchFilters,
      },
    ] as const,
    queryFn: () =>
      fetchData<PaginatedApiResponse<AllTranscriptsProps>>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATIONS(
          projectId!,
          filterId,
          language,
          conversationFilter,
          paginationConfig,
          searchFilters,
          sortConfig,
        ),
        axiosInstanceAnalyze,
      ),
    select: ({ data, pagination }) => ({ items: data, pagination }),
    enabled: shouldFetch && !!projectId,
    staleTime: StaleTime.MEDIUM,
    refetchInterval,
    // Keep previous data visible while fetching new results for a changed
    // sort/page/filter key. Prevents the table from unmounting (and losing
    // scroll position + filter-input focus) during the loading state.
    placeholderData: (prev) => prev,
  });

export const useMutateGetConversationsPageWithStore = (
  isCompletedTranscriptsView: boolean,
  urlCurrentPage: number,
  resetCompletedConversationsStore = false,
) => {
  const {
    completedConversations,
    incompleteConversations,
    actions: { setCompletedConversations, setIncompleteConversations },
  } = useCurrentConversations();

  return useMutateApiWithStore(
    async (params) => {
      if (!params) throw new Error('No parameters provided for fetching conversations page');
      const {
        projectId,
        filterId,
        language,
        conversationFilter,
        page = 1,
        limit = 20,
        sort_by,
        sort_type,
      } = params as GetConversationsPageParams;

      const paginationConfig = { page, limit };
      const sortConfig: SortConfig = sort_by ? { sort_by, sort_type } : {};
      const response = await fetchData<PaginatedApiResponse<AllTranscriptsProps>>(
        API_ENDPOINTS.RESULTS.GET_CONVERSATIONS(
          projectId,
          filterId,
          language,
          conversationFilter,
          paginationConfig,
          undefined,
          sortConfig,
        ),
        axiosInstanceAnalyze,
      );
      return { items: response.data, pagination: response.pagination };
    },
    [QueryTag.CONVERSATIONS],
    (data) => {
      // Update store with new conversations data
      const { pagination, items } = data;
      const shouldPrepend = urlCurrentPage > +pagination.currentPage;

      if (resetCompletedConversationsStore && isCompletedTranscriptsView) {
        setCompletedConversations(items);
        return;
      }

      if (isCompletedTranscriptsView) {
        const updatedConversations = shouldPrepend
          ? [...items, ...completedConversations]
          : [...completedConversations, ...items];
        setCompletedConversations(updatedConversations);
      } else {
        const updatedConversations = shouldPrepend
          ? [...items, ...incompleteConversations]
          : [...incompleteConversations, ...items];
        setIncompleteConversations(updatedConversations);
      }
    },
  );
};

// ==================== UNPUBLISH PROJECT FEATURE ====================

export interface ConversationCounts {
  active: number;
  completed: number;
  total: number;
}

export const useGetConversationCounts = (
  organizationId?: string,
  workspaceId?: string,
  projectId?: string,
  enabled = true,
) =>
  useQuery<ConversationCounts>({
    queryKey: ['conversationCounts', projectId],
    queryFn: () =>
      fetchData<ConversationCounts>(
        API_ENDPOINTS.PROJECTS.GET_CONVERSATION_COUNTS(organizationId, workspaceId, projectId),
      ),
    enabled: enabled && !!organizationId && !!workspaceId && !!projectId,
    staleTime: StaleTime.SHORT,
  });

export const useGetQuotaStatus = (
  organizationId?: string,
  workspaceId?: string,
  projectId?: string,
  enabled = true,
) =>
  useQuery<QuotaStatusResponse>({
    queryKey: ['quotaStatus', projectId],
    queryFn: () =>
      fetchData<QuotaStatusResponse>(
        API_ENDPOINTS.PROJECTS.GET_QUOTA_STATUS(organizationId, workspaceId, projectId),
      ),
    enabled: enabled && !!organizationId && !!workspaceId && !!projectId,
    staleTime: StaleTime.SHORT,
    refetchInterval: 30000,
  });

export const useGetScreeningStats = (projectId?: string) =>
  useQuery({
    queryKey: QUERY_KEYS.SCREENING_STATS(projectId),
    queryFn: () =>
      fetchData<ScreeningStatsResponse>(
        API_ENDPOINTS.RESULTS.GET_SCREENING_STATS(projectId!),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId,
    staleTime: StaleTime.MEDIUM,
  });

// ── TELV1-332: Distinctive concepts (concept cloud) ───────────────────────

/** Sentiment polarity attached to a concept by the analyzer. */
export type ConceptSentiment = 'positive' | 'negative' | 'neutral' | 'mixed';

export interface ConceptEntry {
  /** Lowercased 2-4 word noun phrase. */
  phrase: string;
  /** Primary size weight — distinct respondents whose words/themes support the concept. */
  unique_respondents: number;
  /** Total mentions (mainly informational). */
  total_mentions: number;
  sentiment: ConceptSentiment;
  /** Sorted list of supporting respondent_indexes for click-through to evidence. */
  supporting_respondent_indexes: number[];
  /** Provenance — useful for tooltips and tests. */
  source: 'theme' | 'llm';
}

export interface QuestionConceptsResponse {
  concepts: ConceptEntry[];
  scope: {
    question_id: string | null;
    filter_id: string | null;
    /** Denominator displayed in the cloud footer. */
    total_unique_respondents: number;
  };
}

/**
 * Fetch the distinctive concepts for a single question.
 *
 * Endpoint: GET /analyzer/concepts/:projectId/question/:questionId
 * Query params: filter_id (optional), limit (optional, default 25 on BE)
 *
 * The cache key is scoped to projectId + questionId + filterId + limit so
 * different filter/limit combinations are cached independently.
 */
export const useGetQuestionConcepts = (
  projectId?: string,
  questionId?: string,
  filterId?: string | null,
  limit?: number,
) =>
  useQuery<QuestionConceptsResponse>({
    queryKey: ['question-concepts', projectId, questionId, filterId ?? null, limit ?? null],
    queryFn: () =>
      fetchData<QuestionConceptsResponse>(
        API_ENDPOINTS.CONCEPTS.GET_BY_QUESTION(projectId!, questionId!, filterId, limit),
        axiosInstanceAnalyze,
      ),
    enabled: !!projectId && !!questionId,
    staleTime: StaleTime.LONG,
  });
